import {
	ApiError,
	type OrderLogEntry,
	type OrderLogPage,
	type PortalOrder,
	type PortalOrderLogTab,
} from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { formatDate, formatDateTime } from "@khazzin/i18n/dates";
import { formatMoney } from "@khazzin/i18n/money";
import { CustomerMessagesPanel, OrderCardsSummary } from "@khazzin/ui/patterns";
import {
	DetailLayout,
	EmptyState,
	ErrorState,
	KeyValueList,
	NotFoundState,
	PageHeader,
	Timeline,
	type TimelineItem,
} from "@khazzin/ui/patterns/page";
import { Button, SkeletonText, TabPanel, Tabs } from "@khazzin/ui/primitives";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { useApi } from "../../api";
import { named } from "../../app/named";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf } from "../../app/shell/sections";
import { OrderShipments, RequestReturnButton } from "../returns/OrderReturns";
import { ReportProblemButton } from "../tickets/OpenTicketDrawer";
import { CancelOrderButton, EditOrderLink, WithdrawButton } from "./actions";
import { ContactChip, OrderStatusCell } from "./chips";

/**
 * One order, as the store reads it (EP10-S03, DA-10-05): the header, the
 * lines, and four logs — the status timeline in the seventeen labels the Admin
 * Console uses, the delivery timeline, the Contact log behind the read-only
 * chip, and the store's own edits.
 *
 * Another store's order — or, for an own-created account, one it did not
 * create — is the server's `404`, and reads here as not found. Report a
 * problem opens a ticket about the order (EP10-S12). EP10-S14 adds Request
 * return on a delivered order, the shipments with Recall and Approve RTO, and
 * the Proof of delivery card.
 *
 * At Orders = write a `pending` order carries Edit, the circle withdraw and
 * Cancel, and a withdrawn one Resubmit (EP10-S05).
 */

export const PORTAL_LOG_TABS: readonly PortalOrderLogTab[] = [
	"status",
	"delivery",
	"contact",
	"edit",
];

export function OrderDetailPage() {
	const { t, locale } = useLocale();
	const api = useApi();
	const { orderId = "" } = useParams();
	const { store, context } = useStoreScope();
	const storeId = store?.storeId ?? "";
	const level = levelOf(store, "orders");
	const timeZone = context?.preferences?.timezone ?? "Asia/Beirut";

	const detail = useQuery({
		queryKey: ["portal", storeId, "orders", "detail", orderId],
		queryFn: ({ signal }) => api.getPortalOrder(orderId, { signal }),
		enabled: level !== "none" && storeId !== "",
		retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
	});

	if (level === "none") {
		return <NotFound />;
	}
	if (detail.error instanceof ApiError && detail.error.status === 404) {
		return <NotFoundState t={t} />;
	}
	if (detail.isError) {
		return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} t={t} />;
	}
	if (detail.data === undefined) {
		return <SkeletonText lines={6} />;
	}

	const d: PortalOrder = detail.data;
	const o = d.order;
	const at = (value: string | null | undefined) =>
		value === null || value === undefined ? "—" : formatDateTime(value, { locale, timeZone });
	const money = (minor: number) =>
		formatMoney({ amountMinor: minor, currency: o.currency }, locale);
	const dash = (value: string | null | undefined) =>
		value === null || value === undefined || value === "" ? "—" : value;

	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={o.orderNo}
				backTo="/orders"
				backLabel={t("portal.orders.detail.back")}
				chip={<OrderStatusCell row={o} />}
				secondaryActions={
					<>
						<ReportProblemButton source="order" id={o.orderId} label={o.orderNo} />
						<RequestReturnButton order={d} />
						{level === "write" && !d.withdrawn && o.status === "pending" && (
							<CancelOrderButton
								orderId={o.orderId}
								orderNo={o.orderNo}
								version={d.version}
								storeId={storeId}
							/>
						)}
					</>
				}
				{...(level !== "write"
					? {}
					: d.withdrawn
						? { primaryAction: <EditOrderLink orderId={o.orderId} resubmit /> }
						: o.status === "pending"
							? {
									primaryAction: (
										<div className="flex items-center gap-3">
											<EditOrderLink orderId={o.orderId} />
											<WithdrawButton orderId={o.orderId} orderNo={o.orderNo} storeId={storeId} />
										</div>
									),
								}
							: {})}
				{...(o.channelCustomId === undefined || o.channelCustomId === null
					? {}
					: { meta: o.channelCustomId })}
			/>
			<DetailLayout
				rail={
					<section aria-label={t("portal.orders.detail.summary")} className="card p-4">
						<KeyValueList
							rows={[
								{
									label: t("portal.orders.detail.contact"),
									value: <ContactChip status={o.customerContactStatus} />,
								},
								{ label: t("portal.orders.detail.customer"), value: o.customerName },
								{ label: t("portal.orders.detail.phone"), value: d.customerPhone, mono: true },
								{ label: t("portal.orders.detail.email"), value: dash(d.customerEmail) },
								{ label: t("portal.orders.detail.address"), value: dash(d.addressLine) },
								{ label: t("portal.orders.detail.city"), value: dash(o.cityName) },
								{ label: t("portal.orders.detail.province"), value: dash(o.provinceName) },
								{
									label: t("portal.orders.detail.deliveryCompany"),
									value: dash(o.deliveryCompanyName),
								},
								{
									label: t("portal.orders.detail.deliveryDate"),
									value: d.deliveryDate ? formatDate(d.deliveryDate, { locale, timeZone }) : "—",
								},
								{
									label: t("portal.orders.detail.channel"),
									value: t(`orders.channel.${o.channel}`),
								},
								{ label: t("portal.orders.detail.type"), value: t(`orders.type.${o.type}`) },
								{
									label: t("portal.orders.detail.urgency"),
									value: t(`orders.urgency.${o.urgency}`),
								},
								{
									label: t("portal.orders.detail.payment"),
									value: t(`portal.orders.detail.payment.${d.paymentMethod}`),
								},
								{ label: t("portal.orders.detail.total"), value: money(o.totalMinor) },
								{ label: t("portal.orders.detail.goods"), value: money(d.goodsTotalMinor) },
								{ label: t("portal.orders.detail.packaging"), value: money(d.packagingTotalMinor) },
								...(d.refundedTotalMinor > 0
									? [
											{
												label: t("portal.orders.detail.refunded"),
												value: money(d.refundedTotalMinor),
											},
										]
									: []),
								{ label: t("portal.orders.detail.created"), value: at(o.createdAt) },
								{ label: t("portal.orders.detail.fulfilled"), value: at(o.fulfilledAt) },
								{ label: t("portal.orders.detail.pickedUp"), value: at(d.pickedUpAt) },
								{ label: t("portal.orders.detail.delivered"), value: at(o.deliveredAt) },
								...(d.notes ? [{ label: t("portal.orders.detail.notes"), value: d.notes }] : []),
							]}
						/>
					</section>
				}
			>
				<section aria-labelledby="order-service" className="card p-4">
					<h2 id="order-service" className="mb-3 text-[15px] font-bold text-fg">
						{t("orderCards.detail.title")}
					</h2>
					<OrderCardsSummary order={d} t={t} money={money} />
				</section>
				<section aria-labelledby="order-lines" className="card p-4">
					<h2 id="order-lines" className="mb-3 text-[15px] font-bold text-fg">
						{t("portal.orders.detail.lines")}
					</h2>
					<div className="overflow-x-auto">
						<table className="w-full text-[13px]">
							<thead className="text-start text-fg-muted">
								<tr>
									<th className="py-1 text-start font-semibold">
										{t("portal.orders.detail.line.sku")}
									</th>
									<th className="py-1 text-start font-semibold">
										{t("portal.orders.detail.line.title")}
									</th>
									<th className="py-1 text-end font-semibold">
										{t("portal.orders.detail.line.qty")}
									</th>
									<th className="py-1 text-end font-semibold">
										{t("portal.orders.detail.line.unitPrice")}
									</th>
									<th className="py-1 text-end font-semibold">
										{t("portal.orders.detail.line.delivered")}
									</th>
									<th className="py-1 text-end font-semibold">
										{t("portal.orders.detail.line.returned")}
									</th>
								</tr>
							</thead>
							<tbody>
								{d.lines.map((line) => (
									<tr key={line.orderLineId} className="border-t border-border">
										<td className="py-1.5 font-mono">{line.sku}</td>
										<td className="py-1.5">{dash(line.title)}</td>
										<td className="py-1.5 text-end">{line.qtyOrdered}</td>
										<td className="py-1.5 text-end">
											{formatMoney(
												{ amountMinor: line.unitPriceMinor, currency: line.currency },
												locale,
											)}
										</td>
										<td className="py-1.5 text-end">{line.qtyDelivered}</td>
										<td className="py-1.5 text-end">{line.qtyReturned}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</section>

				<OrderCharges orderId={o.orderId} storeId={storeId} />

				<OrderShipments orderId={o.orderId} storeId={storeId} timeZone={timeZone} />

				<PackPhoto orderId={o.orderId} storeId={storeId} timeZone={timeZone} />

				<CustomerMessages orderId={o.orderId} storeId={storeId} timeZone={timeZone} />

				<section aria-labelledby="order-history" className="card p-4">
					<h2 id="order-history" className="mb-3 text-[15px] font-bold text-fg">
						{t("portal.orders.detail.history")}
					</h2>
					<OrderLogs orderId={o.orderId} storeId={storeId} timeZone={timeZone} />
				</section>
			</DetailLayout>
		</div>
	);
}

/**
 * The parcel's photo from the pack station (EP07-S21): what went into the box,
 * for the day a customer says something did not. Nothing when there is none —
 * the warehouse may not photograph, and a photo past retention is gone.
 */
/**
 * What the store is billed for this order (ADJ-R01-12, EP07-S19's store
 * half): the fees, the packaging charge and the delivery price, and their
 * total. Until the order is final the amounts are an estimate at today's
 * rates, and the panel says so. A charge with no rate is shown as zero with
 * that reason; a waived one as waived.
 */
export function OrderCharges({ orderId, storeId }: { orderId: string; storeId: string }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const charges = useQuery({
		queryKey: ["portal", storeId, "orders", "charges", orderId],
		queryFn: ({ signal }) => api.getPortalOrderCharges(orderId, { signal }),
		retry: false,
	});
	const data = charges.data;
	if (data === undefined || data.status === "void") {
		return null;
	}
	const money = (amountMinor: number) =>
		formatMoney({ amountMinor, currency: data.currency }, locale);
	return (
		<section aria-labelledby="order-charges" className="card p-4">
			<h2 id="order-charges" className="mb-3 text-[15px] font-bold text-fg">
				{t("portal.orders.detail.charges")}
			</h2>
			{data.charges.length === 0 ? (
				<p className="text-[13px] text-fg-muted">{t("portal.orders.detail.charges.none")}</p>
			) : (
				<KeyValueList
					rows={[
						...data.charges.map((charge) => ({
							label: t(`portal.orders.detail.charges.type.${charge.type}`),
							value:
								charge.flag === null || charge.flag === undefined
									? money(charge.amountMinor)
									: `${money(charge.amountMinor)} · ${t(`portal.orders.detail.charges.flag.${charge.flag}`)}`,
						})),
						{ label: t("portal.orders.detail.charges.total"), value: money(data.totalMinor) },
					]}
				/>
			)}
			{data.status !== "final" && (
				<p className="mt-2 text-[12px] text-fg-muted">
					{t("portal.orders.detail.charges.estimate")}
				</p>
			)}
		</section>
	);
}

function PackPhoto({
	orderId,
	storeId,
	timeZone,
}: {
	orderId: string;
	storeId: string;
	timeZone: string;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const photo = useQuery({
		queryKey: ["portal", storeId, "orders", "packPhoto", orderId],
		queryFn: ({ signal }) => api.getPortalOrderPackPhoto(orderId, { signal }),
		retry: false,
		// The link is signed for five minutes.
		staleTime: 4 * 60_000,
	});
	const url = photo.data?.url;
	if (photo.data === undefined || url === undefined) {
		return null;
	}
	return (
		<section aria-labelledby="order-pack-photo" className="card p-4">
			<h2 id="order-pack-photo" className="mb-3 text-[15px] font-bold text-fg">
				{t("portal.orders.detail.packPhoto")}
			</h2>
			<figure className="flex flex-col gap-1.5">
				<a href={url} target="_blank" rel="noreferrer" className="self-start">
					<img
						src={url}
						alt={t("portal.orders.detail.packPhoto")}
						className="max-h-72 rounded-md border border-border"
					/>
				</a>
				<figcaption className="text-[12px] text-fg-muted">
					{t("portal.orders.detail.packPhotoTaken", {
						at: formatDateTime(photo.data.takenAt, { locale, timeZone }),
					})}
				</figcaption>
			</figure>
		</section>
	);
}

/** The messages the store's customer was sent for this order (EP12-S09). */
function CustomerMessages({
	orderId,
	storeId,
	timeZone,
}: {
	orderId: string;
	storeId: string;
	timeZone: string;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const messages = useQuery({
		queryKey: ["portal", storeId, "orders", "customerMessages", orderId],
		queryFn: ({ signal }) => api.listPortalOrderCustomerMessages(orderId, { signal }),
	});
	return (
		<CustomerMessagesPanel
			timeline={messages.data}
			error={messages.error}
			onRetry={() => void messages.refetch()}
			t={t}
			formatTime={(iso) => formatDateTime(iso, { locale, timeZone })}
		/>
	);
}

/** The four logs a store reads, one tab each; a tab is fetched when it is opened. */
function OrderLogs({
	orderId,
	storeId,
	timeZone,
}: {
	orderId: string;
	storeId: string;
	timeZone: string;
}) {
	const { t } = useLocale();
	const [tab, setTab] = useState<PortalOrderLogTab>("status");
	return (
		<Tabs
			aria-label={t("portal.orders.detail.history")}
			value={tab}
			onValueChange={(value) => setTab(value as PortalOrderLogTab)}
			tabs={PORTAL_LOG_TABS.map((value) => ({ value, label: t(`portal.orders.logs.${value}`) }))}
		>
			{PORTAL_LOG_TABS.map((value) => (
				<TabPanel key={value} value={value}>
					{tab === value && (
						<LogTimeline orderId={orderId} storeId={storeId} tab={value} timeZone={timeZone} />
					)}
				</TabPanel>
			))}
		</Tabs>
	);
}

function LogTimeline({
	orderId,
	storeId,
	tab,
	timeZone,
}: {
	orderId: string;
	storeId: string;
	tab: PortalOrderLogTab;
	timeZone: string;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const log = useInfiniteQuery({
		queryKey: ["portal", storeId, "orders", "log", orderId, tab],
		queryFn: ({ pageParam, signal }) =>
			api.getPortalOrderLog(orderId, tab, {
				...(pageParam === "" ? {} : { cursor: pageParam }),
				signal,
			}),
		initialPageParam: "",
		getNextPageParam: (last: OrderLogPage) => last.nextCursor ?? undefined,
	});

	if (log.isError) {
		return <ErrorState error={log.error} onRetry={() => void log.refetch()} t={t} compact />;
	}
	if (log.data === undefined) {
		return <SkeletonText lines={3} />;
	}
	const entries = log.data.pages.flatMap((page) => page.entries);
	if (entries.length === 0) {
		return <EmptyState title={t("portal.orders.logs.empty")} illustration="none" />;
	}
	return (
		<div className="flex flex-col gap-3">
			<Timeline
				items={entries.map((entry, index) => logItem(entry, tab, index, t, locale, timeZone))}
			/>
			{log.hasNextPage && (
				<Button
					variant="secondary"
					size="sm"
					className="w-fit"
					onClick={() => void log.fetchNextPage()}
				>
					{t("portal.orders.logs.loadMore")}
				</Button>
			)}
		</div>
	);
}

/**
 * One log entry as a timeline step. A status move is labelled with the
 * seventeen's own words; a contact entry with the contact status's; anything
 * else (a carrier's event, an edit) with its kind.
 */
export function logItem(
	entry: OrderLogEntry,
	tab: PortalOrderLogTab,
	index: number,
	t: Translate,
	locale: ReturnType<typeof useLocale>["locale"],
	timeZone: string,
): TimelineItem {
	const actor =
		entry.actorLabel !== ""
			? entry.actorLabel
			: t(`portal.orders.logs.actor.${entry.actorType ?? "system"}`);
	const to = entry.to ?? "";
	let title: string;
	if (entry.kind === "customer_reply") {
		title = t("portal.orders.logs.customerReply");
	} else if (tab === "status" && to !== "") {
		title = t(`orders.status.${to}`);
	} else if (tab === "contact" && to !== "") {
		title = t(`orders.contactStatus.${to}`);
	} else if (entry.changes !== undefined && entry.changes.length > 0) {
		title = entry.changes
			.map((change) =>
				t("portal.orders.logs.changed", {
					field: named(t, "portal.orders.logs.field", change.field),
				}),
			)
			.join(" · ");
	} else {
		title = named(t, "portal.orders.logs.kind", entry.kind);
	}
	const reason =
		entry.reason === undefined || entry.reason === null || entry.reason === ""
			? ""
			: ` · ${entry.reason}`;
	// EP12-S09: an automatic message's channel and latest receipt.
	const channel = entry.channel ?? "";
	const receipt = entry.deliveryReceipt ?? "";
	const delivered =
		channel === ""
			? ""
			: ` · ${t(`orders.customerMessages.channel.${channel}`)}${
					receipt === "" ? "" : ` · ${t(`orders.customerMessages.status.${receipt}`)}`
				}`;
	return {
		id: entry.entryId,
		title,
		meta: `${formatDateTime(entry.createdAt, { locale, timeZone })} · ${actor}${reason}${delivered}`,
		state: index === 0 ? "current" : "done",
	};
}

export default OrderDetailPage;
